import {
    DropdownData,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    InputType,
} from '@fitogest/reactive-form-builder/interfaces';
import { list } from '@fitogest/reactive-form-builder/utils';
import { UserModel } from 'src/app/models/usuarios/user.model';

export interface UsuariosFormDefinitionContext {
    readonly appName?: string;
    readonly applicationType?: string;
    readonly canRegister?: boolean;
    readonly clienteName?: string;
    readonly showPerfilUnidades?: boolean;
    readonly countryList?: DropdownData<string | null>;
    readonly recordId?: string;
    readonly getExistingUsers?: () => readonly Partial<UserModel>[];
}

export type UsuariosFormDefinitionMode = 'form' | 'summary';

type UserFieldName = keyof UserModel & string;

export class UsuariosFormDefinition {
    public readonly formType: FormType;
    public readonly formFields: Form<UserModel>;
    public readonly fieldNames: FormField<UserModel>[] = [];
    public readonly fieldLabels: string[] = [];
    public model: Partial<UserModel>;

    private readonly appName: string;
    private readonly applicationType: string;
    private readonly canRegister: boolean;
    private readonly clienteName: string;
    private readonly showPerfilUnidades: boolean;
    private readonly countryList: DropdownData<string | null>;

    constructor(
        type: FormType,
        model: Partial<UserModel>,
        private readonly context: UsuariosFormDefinitionContext = {},
        private readonly mode: UsuariosFormDefinitionMode = 'form',
    ) {
        this.formType = type;
        this.model = this.normalizeModel(model);
        this.appName = context.appName ?? '';
        this.applicationType = context.applicationType ?? '';
        this.canRegister = context.canRegister === true;
        this.clienteName = context.clienteName ?? 'Cliente';
        this.showPerfilUnidades = context.showPerfilUnidades === true;
        this.countryList = context.countryList ?? list<string | null>(['...', null]);
        this.formFields = this.createForm();
        this.collectCsvFields();
    }

    private createForm(): Form<UserModel> {
        const isSummary = this.mode === 'summary' || this.formType === FormType.EXPORT;

        return {
            name: isSummary ? 'usuarios-summary' : 'usuarios',
            header: isSummary ? 'Datos del usuario' : this.getFormHeader(),
            type: this.formType,
            sections: isSummary ? this.getSummarySections() : this.getFormSections(),
            table: null,
            uploadButton: { isEnabled: false, label: '' },
            signed: false,
            previousDataTransform: (previousData: UserModel): UserModel => this.normalizeModel(previousData) as UserModel,
        };
    }

    private getFormHeader(): string {
        return this.formType === FormType.CREATION ? 'Crear Usuario' : 'Actualizar Usuario';
    }

    private getFormSections(): FormSection<UserModel>[] {
        const accountMode = (this.canRegister || this.applicationType === 'cropgest') && this.appName !== 'manezylozano';

        return [
            {
                name: 'Datos de cuenta',
                fields: this.getAccountFields(accountMode),
                width: 12,
                isVisible: accountMode,
                order: 1,
            },
            {
                name: 'Datos personales',
                fields: this.getAccountPersonalFields(accountMode),
                width: 12,
                isVisible: accountMode,
                order: 2,
            },
            {
                name: 'Datos principales',
                fields: this.getBasicPersonalFields(!accountMode),
                width: 12,
                isVisible: !accountMode,
                order: 3,
            },
            {
                name: 'Datos adicionales',
                fields: this.getAdditionalFields(!accountMode),
                width: 12,
                isVisible: !accountMode,
                order: 4,
            },
            {
                name: 'Acceso',
                fields: this.getLoginFields(!accountMode && this.applicationType !== 'cropgest'),
                width: 12,
                isVisible: !accountMode && this.applicationType !== 'cropgest',
                order: 5,
            },
            {
                name: 'Rol',
                fields: this.getRoleFields(!accountMode),
                width: 12,
                isVisible: !accountMode,
                order: 6,
            },
        ];
    }

    private getSummarySections(): FormSection<UserModel>[] {
        return this.getFormSections()
            .map((section) => ({
                ...section,
                fields: section.fields
                    .filter((field) => this.isSummaryField(field))
                    .map((field) => this.toSummaryField(field)),
                isVisible: true,
            }))
            .filter((section) => section.fields.length > 0);
    }

    private isSummaryField(field: FormField<UserModel>): boolean {
        const hiddenFields = new Set<string>([
            'id',
            'password',
            'password_repeat',
            'password_old',
            'sin_contraseña',
        ]);

        return field.conditions.isVisible
            && !hiddenFields.has(field.name)
            && field.input.type !== InputType.EMPTY
            && field.input.type !== InputType.HIDDEN
            && field.input.type !== InputType.PASSWORD;
    }

    private toSummaryField(field: FormField<UserModel>): FormField<UserModel> {
        return {
            ...field,
            input: {
                ...field.input,
                isEditable: false,
            },
            conditions: this.getConditions(true),
            required: false,
            visible: true,
        };
    }

    private getAccountFields(isSectionVisible: boolean): FormField<UserModel>[] {
        return [
            {
                name: 'id',
                field: 'id',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.formType === FormType.CREATION ? '' : this.resolveRecordId(),
                },
                width: 1,
                conditions: this.getConditions(true),
                required: false,
                visible: true,
                order: 0,
            },
            {
                name: 'username',
                field: 'username',
                label: 'Nombre de Usuario',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('username'),
                },
                checkForDuplicates: (value: string): boolean => this.isDuplicateUserValue('username', value),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 1,
            },
            {
                name: 'password',
                field: 'password',
                label: 'Contraseña',
                input: {
                    isEditable: true,
                    type: InputType.PASSWORD,
                    defaultValue: this.getModelValue('password'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 2,
            },
            {
                name: 'password_repeat',
                field: 'password_repeat',
                label: 'Repetir contraseña',
                input: {
                    isEditable: true,
                    type: InputType.PASSWORD,
                    defaultValue: this.getModelValue('password_repeat'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 3,
            },
            {
                name: 'tipo',
                field: 'tipo',
                label: 'Tipo',
                input: {
                    isEditable: true,
                    type: InputType.DROPDOWN,
                    defaultValue: this.getModelValue('tipo'),
                    data: this.getUserRolesCropGest(),
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                values: this.getUserRolesCropGest(),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 4,
            },
            {
                name: 'sin_contraseña',
                field: 'sin_contraseña',
                label: '',
                input: {
                    isEditable: false,
                    type: InputType.HIDDEN,
                    defaultValue: this.getModelValue('sin_contraseña'),
                },
                width: 1,
                conditions: this.getConditions(false),
                required: false,
                visible: false,
                order: 5,
            },
        ];
    }

    private getAccountPersonalFields(isSectionVisible: boolean): FormField<UserModel>[] {
        return [
            {
                name: 'nombre',
                field: 'nombre',
                label: 'Nombre',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('nombre'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 1,
            },
            {
                name: 'apellidos',
                field: 'apellidos',
                label: 'Apellidos',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('apellidos'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 2,
            },
            {
                name: 'email',
                field: 'email',
                label: 'Email',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('email'),
                },
                checkForDuplicates: (value: string): boolean => this.isDuplicateUserValue('email', value),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 3,
            },
            {
                name: 'telefono',
                field: 'telefono',
                label: 'Teléfono',
                input: {
                    isEditable: true,
                    type: InputType.TEXT_MASK,
                    defaultValue: this.getModelValue('telefono'),
                    textMaskConfiguration: { mask: '999-999-999' },
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 4,
            },
            {
                name: 'nif',
                field: 'nif',
                label: 'NIF / CIF',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('nif'),
                    maxLength: 9,
                },
                checkForDuplicates: (value: string): boolean => this.isDuplicateUserValue('nif', value),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 5,
            },
            {
                name: 'tipo_carnet',
                field: 'tipo_carnet',
                label: 'Tipo de carnet',
                input: {
                    isEditable: true,
                    type: InputType.DROPDOWN,
                    defaultValue: this.getModelValue('tipo_carnet'),
                    data: this.getTipoCarnetOptions(),
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                values: this.getTipoCarnetOptions(),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 7,
            },
            {
                name: 'fecha_caducidad',
                field: 'fecha_caducidad',
                label: 'Fecha de caducidad',
                input: {
                    isEditable: true,
                    type: InputType.CALENDAR,
                    defaultValue: this.getModelValue('fecha_caducidad'),
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 8,
            },
            {
                name: 'ropo',
                field: 'ropo',
                label: 'ROPO',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('ropo'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 9,
            },
        ];
    }

    private getBasicPersonalFields(isSectionVisible: boolean): FormField<UserModel>[] {
        return [
            {
                name: 'id',
                field: 'id',
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false,
                    defaultValue: this.formType === FormType.CREATION ? '' : this.resolveRecordId(),
                },
                width: 1,
                conditions: this.getConditions(false),
                required: false,
                visible: false,
                order: 0,
            },
            {
                name: 'email',
                field: 'email',
                label: 'Email',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('email'),
                },
                checkForDuplicates: (value: string): boolean => this.isDuplicateUserValue('email', value),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 1,
            },
            {
                name: 'nombre',
                field: 'nombre',
                label: 'Nombre',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('nombre'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 2,
            },
            {
                name: 'apellidos',
                field: 'apellidos',
                label: 'Apellidos',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('apellidos'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 3,
            },
            {
                name: 'tipo_carnet',
                field: 'tipo_carnet',
                label: 'Tipo de carnet',
                input: {
                    isEditable: true,
                    type: InputType.DROPDOWN,
                    defaultValue: this.getModelValue('tipo_carnet'),
                    data: this.getTipoCarnetOptions(),
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                values: this.getTipoCarnetOptions(),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 4,
            },
            {
                name: 'fecha_caducidad',
                field: 'fecha_caducidad',
                label: 'Fecha de caducidad',
                input: {
                    isEditable: true,
                    type: InputType.CALENDAR,
                    defaultValue: this.getModelValue('fecha_caducidad'),
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 5,
            },
            {
                name: 'nif',
                field: 'nif',
                label: 'NIF',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('nif'),
                    maxLength: 9,
                },
                checkForDuplicates: (value: string): boolean => this.isDuplicateUserValue('nif', value),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 6,
            },
            {
                name: 'ropo',
                field: 'ropo',
                label: 'ROPO',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('ropo'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 7,
            },
            {
                name: 'imei',
                field: 'imei',
                label: 'IMEI',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('imei'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible && this.appName === 'manezylozano'),
                required: false,
                visible: isSectionVisible && this.appName === 'manezylozano',
                order: 8,
            },
            {
                name: 'pais',
                field: 'pais',
                label: 'País',
                input: {
                    isEditable: true,
                    type: InputType.DROPDOWN_SEARCH,
                    defaultValue: this.getModelValue('pais'),
                    data: this.countryList,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                values: this.countryList,
                width: 3,
                conditions: this.getConditions(isSectionVisible && this.appName === 'manezylozano'),
                required: false,
                visible: isSectionVisible && this.appName === 'manezylozano',
                order: 9,
            },
        ];
    }

    private getAdditionalFields(isSectionVisible: boolean): FormField<UserModel>[] {
        const unitOptions = list<string | null>('Hectáreas', 'Hanegadas', 'Taullas');
        const commandOptions = this.getTipoMandoOptions();

        return [
            {
                name: 'uds_medida',
                field: 'uds_medida',
                label: 'Unidad de medida',
                input: {
                    isEditable: true,
                    type: InputType.DROPDOWN,
                    defaultValue: this.getModelValue('uds_medida'),
                    data: unitOptions,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                values: unitOptions,
                width: 3,
                conditions: this.getConditions(isSectionVisible && this.showPerfilUnidades),
                required: false,
                visible: isSectionVisible && this.showPerfilUnidades,
                order: 1,
            },
            {
                name: 'ratio_ha',
                field: 'ratio_ha',
                label: 'Relación con una hectárea',
                input: {
                    isEditable: true,
                    type: InputType.NUMERIC,
                    defaultValue: this.getModelValue('ratio_ha'),
                    placeholder: '1 Hectárea = X unidades',
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible && this.showPerfilUnidades),
                required: false,
                visible: isSectionVisible && this.showPerfilUnidades,
                order: 2,
            },
            {
                name: 'tipo_mando',
                field: 'tipo_mando',
                label: 'Tipo de mando',
                input: {
                    isEditable: true,
                    type: InputType.DROPDOWN,
                    defaultValue: this.getModelValue('tipo_mando'),
                    data: commandOptions,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                values: commandOptions,
                width: 3,
                conditions: this.getConditions(isSectionVisible && this.appName === 'manezylozano'),
                required: false,
                visible: isSectionVisible && this.appName === 'manezylozano',
                order: 3,
            },
        ];
    }

    private getLoginFields(isSectionVisible: boolean): FormField<UserModel>[] {
        return [
            {
                name: 'email',
                field: 'email',
                label: 'Email',
                input: {
                    isEditable: true,
                    type: InputType.TEXT,
                    defaultValue: this.getModelValue('email'),
                },
                checkForDuplicates: (value: string): boolean => this.isDuplicateUserValue('email', value),
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 1,
            },
            {
                name: 'password',
                field: 'password',
                label: 'Contraseña',
                input: {
                    isEditable: true,
                    type: InputType.PASSWORD,
                    defaultValue: this.getModelValue('password'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 2,
            },
            {
                name: 'password_repeat',
                field: 'password_repeat',
                label: 'Repetir contraseña',
                input: {
                    isEditable: true,
                    type: InputType.PASSWORD,
                    defaultValue: this.getModelValue('password_repeat'),
                },
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 3,
            },
        ];
    }

    private getRoleFields(isSectionVisible: boolean): FormField<UserModel>[] {
        const roleOptions = this.getRoleOptions();

        return [
            {
                name: 'rol',
                field: 'rol',
                label: 'Rol del usuario',
                input: {
                    isEditable: true,
                    type: InputType.DROPDOWN,
                    defaultValue: this.getModelValue('rol'),
                    data: roleOptions,
                    optionLabel: 'label',
                    optionValue: 'value',
                },
                values: roleOptions,
                width: 3,
                conditions: this.getConditions(isSectionVisible),
                required: false,
                visible: isSectionVisible,
                order: 1,
            },
        ];
    }

    private getConditions(isVisible = true, isRequired = false): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible,
        };
    }

    private getRoleOptions(): DropdownData<string | null> {
        if (this.appName === 'dqvademecum') {
            return list(['Usuario normal', 'user'], ['Trabajador DQAgro', 'admin']);
        }

        if (this.applicationType === 'cropgest') {
            return this.getUserRolesCropGest();
        }

        return list(['Usuario normal', 'user'], ['Administrador', 'admin']);
    }

    private getUserRolesCropGest(): DropdownData<string | null> {
        let applicatorLabel = 'Aplicador';

        if (this.appName === 'avafitogest') {
            applicatorLabel = 'Técnico';
        } else if (this.appName === 'benibal') {
            applicatorLabel = 'Asesor';
        }

        return list(['...', null], [this.clienteName, 'comunero'], [applicatorLabel, 'usuario']);
    }

    private getTipoCarnetOptions(): DropdownData<string | null> {
        return list(['...', null], 'BÁSICO', 'CUALIFICADO', 'FUMIGADOR', 'PILOTO', 'ASESOR');
    }

    private getTipoMandoOptions(): DropdownData<string | null> {
        return list(['...', null], 'SISTRONIC', 'TRONIC', 'SISTROMATIC', 'OZONO');
    }

    private isDuplicateUserValue(fieldName: 'username' | 'email' | 'nif', value: string): boolean {
        const normalizedValue = this.normalizeDuplicateFieldValue(fieldName, value);

        if (normalizedValue === '') {
            return false;
        }

        return this.getExistingUsers().some((user) => {
            if (this.isCurrentRecord(user)) {
                return false;
            }

            return this.getDuplicateFieldCandidates(user, fieldName).some((candidate) =>
                this.normalizeDuplicateFieldValue(fieldName, candidate) === normalizedValue
            );
        });
    }

    private getExistingUsers(): readonly Partial<UserModel>[] {
        return this.context.getExistingUsers?.() ?? [];
    }

    private isCurrentRecord(user: Partial<UserModel>): boolean {
        const currentRecordId = this.resolveRecordId();

        if (currentRecordId === '') {
            return false;
        }

        return [user.id_plataforma, user.id].some((value) => String(value ?? '').trim() === currentRecordId);
    }

    private getDuplicateFieldCandidates(user: Partial<UserModel>, fieldName: 'username' | 'email' | 'nif'): readonly unknown[] {
        if (fieldName === 'nif') {
            return [user.nif, user.cif];
        }

        return [user[fieldName]];
    }

    private normalizeDuplicateFieldValue(fieldName: 'username' | 'email' | 'nif', value: unknown): string {
        if (fieldName === 'nif') {
            return this.normalizeSpanishId(value);
        }

        return String(value ?? '').trim().toLowerCase();
    }

    private collectCsvFields(): void {
        this.fieldNames.push(...this.formFields.sections.flatMap((section) => section.fields).filter((field) => {
            return field.conditions.isVisible && field.input.type !== InputType.EMPTY && field.input.type !== InputType.HIDDEN;
        }));
        this.fieldLabels.push(...this.fieldNames.map((field) => field.label));
    }

    private normalizeModel(model: Partial<UserModel>): Partial<UserModel> {
        const normalizedModel = { ...model };
        const recordId = this.resolveRecordId(normalizedModel.id_plataforma, normalizedModel.id);

        if (recordId !== '') {
            normalizedModel.id = recordId;
            normalizedModel.id_plataforma = recordId;
        }

        if (normalizedModel.nif !== undefined && normalizedModel.nif !== null) {
            normalizedModel.nif = this.normalizeSpanishId(normalizedModel.nif);
        }

        if (normalizedModel.cif !== undefined && normalizedModel.cif !== null) {
            normalizedModel.cif = this.normalizeSpanishId(normalizedModel.cif);
        }

        const password = this.getPasswordForEdition(normalizedModel);

        if (password !== '') {
            normalizedModel.password = password;
            normalizedModel.password_repeat = password;
        }

        if (this.applicationType === 'cropgest' && String(normalizedModel.tipo ?? '').trim() === '') {
            normalizedModel.tipo = 'usuario';
        }

        return normalizedModel;
    }

    private normalizeSpanishId(value: unknown): string {
        return String(value ?? '').replace(/[- ./]/g, '').trim().toUpperCase();
    }

    private getPasswordForEdition(model: Partial<UserModel>): string {
        const values = [model.password, model.password_repeat, model.password_old];

        for (const value of values) {
            const password = String(value ?? '').trim();

            if (password !== '' && password !== '...' && password !== 'Sin Contraseña') {
                return password;
            }
        }

        return '';
    }

    private getModelValue(name: UserFieldName): string | number | boolean | Date | UserModel | null | undefined {
        return this.model[name] as string | number | boolean | Date | UserModel | null | undefined;
    }

    private resolveRecordId(...candidates: readonly unknown[]): string {
        const values = [
            ...candidates,
            this.model?.id_plataforma,
            this.model?.id,
            this.context.recordId,
        ];

        for (const value of values) {
            const recordId = String(value ?? '').trim();

            if (recordId !== '') {
                return recordId;
            }
        }

        return '';
    }
}
